import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Mail, Plus, QrCode } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg } from "../../lib/api";
import { shortDate } from "../../lib/format";
import { DataTable, PageHeader, StatusPill, TestBadge } from "../../components/admin/ui";
import { CreateBatchDialog, IssuedDialog } from "../../components/admin/InvitationDialogs";
import { download } from "../../lib/download";

const Stat = ({ s }) => (
  <span className="whitespace-nowrap text-xs" data-testid="batch-stats">
    <b>{s.total}</b> issued total · <b className="text-green-700">{s.redeemed}</b> redeemed · <b>{s.remaining}</b> remaining
    {s.revoked > 0 && ` · ${s.revoked} revoked`}{s.expired > 0 && ` · ${s.expired} expired`}
  </span>
);

function BatchInvitations({ batch, canEdit, onChanged }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ status: "", q: "" });
  const load = useCallback(() => api.get("/admin/invitations", { params: { batch_id: batch.id, ...f } }).then((r) => setRows(r.data)), [batch.id, f]);
  useEffect(() => { load(); }, [load]);
  const act = async (r, action) => {
    if (action === "revoke" && !window.confirm("Revoke this invitation? The link will stop working.")) return;
    if (action === "resend" && !window.confirm(`Email this invitation to ${r.invitee_email || "(no email)"} now?`)) return;
    try {
      const { data } = await api.post(`/admin/invitations/${r.id}/${action}`);
      if (action === "revoke") toast.success("Revoked");
      else (data.email_status === "sent" ? toast.success : toast.error)(`Email status: ${data.email_status} (see Email Communications)`);
      load(); onChanged();
    }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [
    { key: "invitee_name", label: "Invitee", render: (r) => <span className="font-semibold text-navy">{r.invitee_name || "(unnamed)"} <TestBadge show={r.is_test} /></span> },
    { key: "invitee_email", label: "Email" }, { key: "code_display", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code_display}</span> },
    { key: "status", label: "Status", render: (r) => <StatusPill value={r.status} /> }, { key: "exp", label: "Expires", render: (r) => shortDate(r.expires_at) },
    ...(canEdit ? [{ key: "_a", label: "Actions", render: (r) => (
      <div className="flex flex-wrap gap-2">
        {r.status === "issued" && <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => act(r, "resend")} data-testid={`invitation-resend-${r.id}`}>Send email</button>}
        {r.status === "issued" && <button type="button" className="rounded border px-2 py-1 text-xs text-red-700" onClick={() => act(r, "revoke")} data-testid={`invitation-revoke-${r.id}`}>Revoke</button>}
        <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => download(`/admin/invitations/${r.id}/email-preview`, "", true)} data-testid={`invitation-preview-${r.id}`}>Email preview</button>
      </div>) }] : []),
  ];
  return (
    <section className="mt-8" data-testid="batch-invitations">
      <h2 className="text-xl text-navy">{batch.label}</h2>
      <div className="my-3 flex flex-wrap gap-3">
        <input className="field-input max-w-xs py-2 text-sm" placeholder="Search name, email, last 4…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} aria-label="Search invitations" data-testid="invitations-search-input" />
        <select className="field-input max-w-[12rem] py-2 text-sm" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} aria-label="Filter by status" data-testid="invitations-status-filter">
          {["", "issued", "redeemed", "revoked", "expired"].map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
      </div>
      <DataTable columns={cols} rows={rows} testid="invitations-table" empty="No invitations match." />
    </section>
  );
}

export default function Invitations() {
  const { canEdit } = useAuth();
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState(null);
  const [exportsLog, setExportsLog] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/invitation-batches").then((r) => setBatches(r.data));
    api.get("/admin/invitation-exports").then((r) => setExportsLog(r.data.slice(0, 10)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const send = async (b) => {
    if (!window.confirm(`Email every issued invitation in "${b.label}" that has an email address?`)) return;
    try {
      const { data } = await api.post(`/admin/invitation-batches/${b.id}/send`);
      const summary = Object.entries(data.counts).map(([k, n]) => `${n} ${k.replace("_", " ")}`).join(", ") || "nothing to send";
      (data.counts.failed ? toast.error : toast.success)(`Invitations: ${summary}`); load();
    }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [
    { key: "label", label: "Batch", render: (b) => <span className="font-semibold text-navy">{b.label} <TestBadge show={b.is_test} /></span> },
    { key: "category_name", label: "Category" }, { key: "sponsor_name", label: "Sponsor", render: (b) => b.sponsor_name || "—" },
    { key: "stats", label: "Usage", render: (b) => <Stat s={b.stats} /> }, { key: "exp", label: "Expires", render: (b) => shortDate(b.expires_at) },
    ...(canEdit ? [{ key: "_a", label: "Export / send", render: (b) => (
      <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => download(`/admin/invitation-batches/${b.id}/export.csv`, `invitations-${b.id}.csv`).then(load)} data-testid={`batch-export-csv-${b.id}`}><Download size={12} className="inline" aria-hidden="true" /> CSV</button>
        <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => download(`/admin/invitation-batches/${b.id}/qr.zip`, `invitation-qr-${b.id}.zip`).then(load)} data-testid={`batch-export-qr-${b.id}`}><QrCode size={12} className="inline" aria-hidden="true" /> QR ZIP</button>
        <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => download(`/admin/invitation-batches/${b.id}/sheet.html`, "", true).then(load)} data-testid={`batch-export-sheet-${b.id}`}><FileText size={12} className="inline" aria-hidden="true" /> QR sheet</button>
        <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => send(b)} data-testid={`batch-send-${b.id}`}><Mail size={12} className="inline" aria-hidden="true" /> Send emails</button>
      </div>) }] : []),
  ];
  return (
    <div data-testid="admin-invitations">
      <PageHeader title="Invitations" subtitle="Personal single-use invitation codes for complimentary categories (EDLS sponsored attendees, sponsor passes).">
        {canEdit && <button type="button" className="btn-primary btn-sm" onClick={() => setCreating(true)} data-testid="invitations-new-batch-btn"><Plus size={16} aria-hidden="true" /> New batch</button>}
      </PageHeader>
      <p className="mb-4 rounded border border-dashed border-gold bg-[#FFFBF0] p-3 text-sm text-navy" data-testid="invitations-email-note">
        "Send" emails invitations through the SMTP settings in Integrations (each one is logged as sent or failed in Email Communications). Unnamed codes without an email: use the CSV or QR exports (every export is logged).</p>
      <DataTable columns={cols} rows={batches} onRowClick={setSelected} testid="invitation-batches-table" empty="No invitation batches yet." />
      {selected && <BatchInvitations key={selected.id} batch={selected} canEdit={canEdit} onChanged={load} />}
      <section className="mt-8">
        <h2 className="mb-2 text-lg text-navy">Recent code exports (audit)</h2>
        <DataTable testid="invitation-exports-table" rows={exportsLog} empty="No exports yet."
          columns={[{ key: "created_at", label: "When", render: (e) => new Date(e.created_at).toLocaleString() }, { key: "user", label: "User" },
            { key: "kind", label: "Export" }, { key: "batch_label", label: "Batch" }, { key: "count", label: "Codes" }]} />
      </section>
      {creating && <CreateBatchDialog onClose={() => setCreating(false)} onCreated={(d) => { setCreating(false); setIssued(d); load(); }} />}
      {issued && <IssuedDialog data={issued} onClose={() => setIssued(null)} />}
    </div>
  );
}
